// The Awesome Present: personalizer (offer bars + design + child's name + live preview + add to cart).
// The name is optional (unless the block setting "Name is required" is on); when given, it is saved on
// its cart line as the property "Name". The 2nd pillow is made free by the
// automatic discount "Buy 1, Get 1 FREE" (Shopify admin), not by this script.
import { CartLinesUpdateEvent } from '@shopify/events';

const FONT_FAMILY = 'Fredoka';
const FONT_WEIGHT = 700;
const CANVAS_SIZE = 1000;
// Letters (any alphabet), spaces, apostrophes, hyphens and dots only: no emoji, no symbols.
const ALLOWED = /[^\p{L}\p{M} .'’-]/gu;

class TapPersonalizer extends HTMLElement {
  #variants = [];
  #images = new Map();
  #mode = 'double';
  #active = 0;
  #canvas = document.createElement('canvas');
  #bigCanvas = document.createElement('canvas');
  #fontReady = null;
  #renderQueued = false;
  #galleryImg = null;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-tap-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#canvas.width = CANVAS_SIZE;
    this.#canvas.height = CANVAS_SIZE;
    this.#mode = this.querySelector('.tap-offers input:checked')?.value || this.dataset.defaultMode || 'double';
    this.#fontReady = document.fonts?.load(`${FONT_WEIGHT} 100px "${FONT_FAMILY}"`).catch(() => null);

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('focusin', this.#onFocus);
    this.querySelector('[data-atc]')?.addEventListener('click', this.#onAdd);

    this.#applyMode();
    this.#pillows().forEach((p) => this.#updateChosen(p));
    // No preview on load (23/10): every gallery image shows its own photo until a design is picked, a name typed or
    // an offer chosen.
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('focusin', this.#onFocus);
  }

  #pillows() {
    return [...this.querySelectorAll('.tap-pillow')];
  }

  #visiblePillows() {
    return this.#pillows().filter((p) => !p.hidden);
  }

  #variantOf(pillow) {
    const id = Number(pillow.querySelector('.tap-swatches input:checked')?.value);
    return this.#variants.find((v) => v.id === id) || this.#variants[0];
  }

  #nameOf(pillow) {
    return (pillow.querySelector('.tap-name')?.value || '').trim().replace(/\s+/g, ' ');
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.closest('.tap-offers')) {
      this.#mode = target.value;
      this.#applyMode();
      if (this.#mode === 'single') this.#active = 0;
      this.#queueRender();
      return;
    }
    const pillow = target.closest('.tap-pillow');
    if (pillow && target.closest('.tap-swatches')) {
      this.#active = Number(pillow.dataset.index);
      this.#updateChosen(pillow);
      this.#queueRender(true);
    }
  };

  #onInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('tap-name')) return;
    const clean = input.value.replace(ALLOWED, '');
    if (clean !== input.value) {
      const pos = input.selectionStart;
      input.value = clean;
      if (pos != null) input.setSelectionRange(pos - 1, pos - 1);
    }
    const pillow = input.closest('.tap-pillow');
    if (!pillow) return;
    this.#active = Number(pillow.dataset.index);
    const count = pillow.querySelector('[data-count]');
    if (count) count.textContent = `${input.value.length}/${this.dataset.maxLength || 15}`;
    this.#setError(pillow, '');
    this.#queueRender(true);
  };

  #onFocus = (event) => {
    const pillow = event.target instanceof Element ? event.target.closest('.tap-pillow') : null;
    if (!pillow) return;
    const index = Number(pillow.dataset.index);
    if (index !== this.#active) {
      this.#active = index;
      this.#queueRender();
    }
  };

  #applyMode() {
    this.#pillows().forEach((p, i) => {
      p.hidden = this.#mode === 'single' && i > 0;
    });
    this.querySelectorAll('.tap-offer').forEach((label) => {
      label.classList.toggle('is-selected', label.dataset.mode === this.#mode);
    });
  }

  #updateChosen(pillow) {
    const title = pillow.querySelector('[data-chosen-title]');
    if (title) title.textContent = this.#variantOf(pillow)?.title || '';
  }

  #setError(pillow, message) {
    const error = pillow.querySelector('[data-error]');
    const input = pillow.querySelector('.tap-name');
    if (!error || !input) return;
    error.textContent = message;
    error.hidden = !message;
    input.toggleAttribute('aria-invalid', Boolean(message));
  }

  // ---------- Live preview ----------

  #queueRender(showFirstSlide = false) {
    if (this.#renderQueued) return;
    this.#renderQueued = true;
    requestAnimationFrame(async () => {
      this.#renderQueued = false;
      await this.#render();
      if (showFirstSlide) this.#showFirstSlide();
    });
  }

  #loadImage(src) {
    if (!src) return Promise.resolve(null);
    if (!this.#images.has(src)) {
      this.#images.set(
        src,
        new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = src;
        })
      );
    }
    return this.#images.get(src);
  }

  async #render() {
    const pillow = this.#visiblePillows()[this.#active] || this.#pillows()[0];
    if (!pillow) return;
    const variant = this.#variantOf(pillow);
    const name = this.#nameOf(pillow);
    const [img] = await Promise.all([this.#loadImage(variant?.image), this.#fontReady]);

    const ctx = this.#canvas.getContext('2d');
    if (!ctx) return;
    const S = CANVAS_SIZE;
    ctx.clearRect(0, 0, S, S);
    ctx.fillStyle = this.dataset.previewBg || '#FFF1E3';
    ctx.fillRect(0, 0, S, S);

    if (img) {
      const scale = Number(this.dataset.designScale || 84) / 100;
      const w = S * scale;
      const h = (img.naturalHeight / img.naturalWidth) * w;
      ctx.drawImage(img, (S - w) / 2, (S - h) / 2 - S * 0.02, w, h);
    }

    if (name) this.#drawName(ctx, name);

    // Big square next to this pillow's designs (mobile, 23/10): the design and the name, without the note below.
    const big = pillow.querySelector('.tap-swatches__big-img');
    if (big && big.offsetParent !== null) {
      this.#bigCanvas.width = this.#bigCanvas.height = 400;
      this.#bigCanvas.getContext('2d')?.drawImage(this.#canvas, 0, 0, 400, 400);
      big.removeAttribute('srcset');
      big.removeAttribute('sizes');
      big.src = this.#bigCanvas.toDataURL('image/jpeg', 0.86);
    }

    // The note sits between the pillow (bottom at ~82% of the canvas) and the gallery dots, which cover the
    // bottom 7% of the image on mobile.
    const note = this.dataset.note;
    if (note) {
      ctx.font = `500 22px ${FONT_FAMILY}, system-ui, sans-serif`;
      ctx.fillStyle = '#7a6a62';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(note, S / 2, S - 105);
    }

    this.#paintGallery(this.#canvas.toDataURL('image/jpeg', 0.88), variant);
  }

  #drawName(ctx, name) {
    const S = CANVAS_SIZE;
    const boxW = (Number(this.dataset.nameBoxW || 36) / 100) * S;
    const boxH = (Number(this.dataset.nameBoxH || 14) / 100) * S;
    const y = (Number(this.dataset.nameY || 74) / 100) * S;
    let size = boxH;
    const font = (px) => `${FONT_WEIGHT} ${px}px "${FONT_FAMILY}", system-ui, sans-serif`;
    ctx.font = font(size);
    while (size > 12 && ctx.measureText(name).width > boxW) {
      size -= 2;
      ctx.font = font(size);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    ctx.lineWidth = Math.max(4, size * 0.16);
    ctx.strokeStyle = this.dataset.nameStroke || '#FFFFFF';
    ctx.strokeText(name, S / 2, y);
    ctx.fillStyle = this.dataset.nameColor || '#772D1A';
    ctx.fillText(name, S / 2, y);
  }

  // Where the preview is drawn (23/10):
  // - every design has its slide in the gallery (« mets toutes les images dans le carousel … on voit pas les produits
  //   1 par 1 »): the preview goes on the slide of the chosen design, the slide drawn before gets its photo back;
  // - only the selected design is in the gallery (hide_variants): on that slide (data-preview-media);
  // - otherwise on the first gallery image, as before.
  #findGalleryImage(variant) {
    const section = this.closest('.shopify-section') || document;
    let img = null;
    for (const id of [variant?.media, this.dataset.previewMedia]) {
      if (id) img = section.querySelector(`media-gallery [data-media-id="${id}"] img`);
      if (img) break;
    }
    img ||=
      section.querySelector('media-gallery img') ||
      section.querySelector('.product-media img') ||
      section.querySelector('[data-media-id] img');
    if (img && !('tapOriginal' in img.dataset)) {
      img.dataset.tapOriginal = img.getAttribute('src') || '';
      img.dataset.tapSrcset = img.getAttribute('srcset') || '';
      img.dataset.tapSizes = img.getAttribute('sizes') || '';
    }
    return img;
  }

  #restore(img) {
    if (!img || !('tapOriginal' in img.dataset)) return;
    if (img.dataset.tapSrcset) img.setAttribute('srcset', img.dataset.tapSrcset);
    if (img.dataset.tapSizes) img.setAttribute('sizes', img.dataset.tapSizes);
    img.src = img.dataset.tapOriginal;
    const zoom = img.closest('[data-media-id]')?.querySelector('[data-zoom-src], a[href]');
    if (zoom instanceof HTMLAnchorElement && img.dataset.tapOriginal) zoom.href = img.dataset.tapOriginal;
  }

  #paintGallery(dataUrl, variant) {
    const img = this.#findGalleryImage(variant);
    if (!img) return;
    if (this.#galleryImg && this.#galleryImg !== img) this.#restore(this.#galleryImg);
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.loading = 'eager';
    img.src = dataUrl;
    const zoom = img.closest('[data-media-id]')?.querySelector('[data-zoom-src], a[href]');
    if (zoom instanceof HTMLAnchorElement) zoom.href = dataUrl;
    this.#galleryImg = img;
  }

  // Brings the preview slide into view when a design is picked or a name is typed.
  #showFirstSlide() {
    const img = this.#galleryImg;
    if (!img?.isConnected) return;
    const slideshow = img.closest('slideshow-component');
    if (slideshow && typeof slideshow.select === 'function') {
      const slides = [...slideshow.querySelectorAll('slideshow-slide')];
      const index = Math.max(0, slides.indexOf(img.closest('slideshow-slide')));
      try {
        slideshow.select(index, undefined, { animate: false });
      } catch {
        /* the gallery stays where it is */
      }
    }
  }

  // ---------- Add to cart ----------

  #onAdd = async () => {
    const pillows = this.#visiblePillows();
    const required = this.dataset.nameRequired === 'true';
    let firstInvalid = null;
    for (const p of pillows) {
      const name = this.#nameOf(p);
      if (required && !name) {
        this.#setError(p, this.dataset.errorEmpty || 'Please add a name.');
        firstInvalid ||= p;
      } else {
        this.#setError(p, '');
      }
    }
    if (firstInvalid) {
      firstInvalid.querySelector('.tap-name')?.focus();
      return;
    }

    const items = pillows.map((p) => {
      const name = this.#nameOf(p);
      return { id: this.#variantOf(p).id, quantity: 1, ...(name ? { properties: { Name: name } } : {}) };
    });

    const button = /** @type {HTMLButtonElement|null} */ (this.querySelector('[data-atc]'));
    const label = this.querySelector('[data-atc-label]');
    const original = label?.textContent;
    if (button) button.disabled = true;
    if (label) label.textContent = this.dataset.labelAdding || 'Adding…';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const add = async (body) => {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await response.json().catch(() => ({}));
        return { ok: response.ok && !json.status, json };
      };
      // A cart section that fails to render must not stop the add: try again without asking for the sections.
      let { ok, json: data } = await add({ items, sections: sectionIds.join(',') });
      if (!ok) ({ ok, json: data } = await add({ items }));
      if (!ok) {
        const failure = new Error(data.description || data.message || 'Add to cart failed');
        failure.fromShopify = Boolean(data.description || data.message);
        throw failure;
      }

      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'tap-personalizer',
          sourceId: this.dataset.productId,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = 'Added to cart';
    } catch (error) {
      console.error('[tap-personalizer]', error);
      deferred.reject(error);
      // Shopify's own message (e.g. a design out of stock) says more than the generic one.
      const reason = error?.fromShopify ? error.message : '';
      const last = pillows[pillows.length - 1];
      if (last) this.#setError(last, reason || this.dataset.labelError || 'Something went wrong. Please try again.');
    } finally {
      if (button) button.disabled = false;
      if (label && original) label.textContent = original;
    }
  };
}

if (!customElements.get('tap-personalizer')) customElements.define('tap-personalizer', TapPersonalizer);
